export * from 'marketing-platform-lib/utils/filters';
export * from 'framework-listing-lib/earlyRequester/filters';
import { combineFilters, isEmptyFilterGroup, isSameFilterGroup, isSameFilterOperator, removeDuplicatedFilterOperators } from 'framework-listing-lib/earlyRequester/filters';
import { ASC, DESC } from 'framework-listing-lib/constants/sorts';
import { INITIAL_FILTER_STATE } from 'framework-listing-lib/constants/filters';
import { getInitialPagination } from '../internal/utils/pagination';

/**
 * Return the filter value for the most common filter operators (EQ, IN and NOT_IN).
 * @param {FiltersState} filters filters applied to the table.
 * @param {string} property property to return the filter value.
 * @param {any} defaultValue (optional): returned in case the filter is not found for property.
 */
export function getFilterValueByProperty(filters, property, defaultValue) {
  let filterOperator;
  if (!filters.filterGroups) {
    return defaultValue;
  }
  for (const filterGroup of filters.filterGroups) {
    filterOperator = filterGroup.filters.find(filter => filter.property === property);
    if (filterOperator) {
      break;
    }
  }
  if (!filterOperator) {
    return defaultValue;
  }
  if (filterOperator.operator === 'EQ') {
    return filterOperator.value;
  }
  if (filterOperator.operator === 'IN' || filterOperator.operator === 'NOT_IN') {
    return filterOperator.values;
  }
  if (filterOperator.operator === 'NOT_HAS_PROPERTY') {
    return undefined;
  }
  return defaultValue;
}
export function removeFilterOperator(filterGroup, filterOperatorToRemove) {
  return {
    filters: filterGroup.filters.filter(filterOperator => !isSameFilterOperator(filterOperator, filterOperatorToRemove))
  };
}

/**
 * Returns a flat list of filterOperators in @filterGroups
 * @param filterGroups
 */
export function flattenFilterGroups(filterGroups) {
  const allFilterOperators = filterGroups.map(filterGroup => filterGroup.filters).flat();
  return removeDuplicatedFilterOperators(allFilterOperators);
}
export function removeDuplicatedFilterGroups(filterGroups) {
  if (filterGroups.length <= 1) {
    return filterGroups;
  }
  const result = [];
  for (const filterGroup of filterGroups) {
    if (result.length === 0) {
      result.push(filterGroup);
      continue;
    }
    if (result.some(filterGroupInResult => isSameFilterGroup(filterGroupInResult, filterGroup))) {
      continue;
    }
    result.push(filterGroup);
  }
  return result;
}

/**
 * Remove all filter operators for @propertyName
 * @param filterGroups
 * @param propertyName
 */
export function removePropertyFilters(filterGroups, propertyName) {
  const result = [];
  for (const filterGroup of filterGroups) {
    const filters = filterGroup.filters.filter(filter => filter.property !== propertyName);
    if (filters.length === 0) {
      continue;
    }
    result.push({
      filters
    });
  }
  if (result.length === 0) {
    result.push({
      filters: []
    });
  }
  return result;
}
function updateFilterStateWithFilterOperator(previousFilterState, updatedFilter) {
  const updatedFilterGroups = removePropertyFilters(previousFilterState.filterGroups, updatedFilter.property);
  return combineFilters(updatedFilterGroups, [{
    filters: [updatedFilter]
  }]);
}
function updateFilterStateWithFilterGroup(previousFilterState, filterGroups) {
  if (isEmptyFilterGroup(filterGroups)) {
    return previousFilterState.filterGroups;
  }
  const allPropertiesInFilter = flattenFilterGroups(filterGroups).map(filter => filter.property);
  const isSinglePropertyFilter = Array.from(new Set(allPropertiesInFilter)).length === 1;
  if (!isSinglePropertyFilter) {
    return filterGroups;
  }
  const [propertyName] = allPropertiesInFilter;
  const updatedFilterGroups = removePropertyFilters(previousFilterState.filterGroups, propertyName);
  return combineFilters(updatedFilterGroups, filterGroups);
}

/**
 * Refer to useUpdateFilters (onUpdateFilters) for a complete documentation of this method.
 * @param previousFilterState
 * @param updatedFilter
 */
export function updateFilterState(previousFilterState, updatedFilter) {
  if ('operator' in updatedFilter) {
    return Object.assign({}, previousFilterState, {
      filterGroups: updateFilterStateWithFilterOperator(previousFilterState, updatedFilter),
      pagination: {
        count: previousFilterState.pagination.count,
        offset: 0
      }
    });
  } else if (Array.isArray(updatedFilter)) {
    return Object.assign({}, previousFilterState, {
      filterGroups: updateFilterStateWithFilterGroup(previousFilterState, updatedFilter),
      pagination: {
        count: previousFilterState.pagination.count,
        offset: 0
      }
    });
  }
  return Object.assign({}, previousFilterState, updatedFilter, {
    pagination: {
      count: updatedFilter.pagination ? updatedFilter.pagination.count : previousFilterState.pagination.count,
      // Filter changes should reset the page to 0
      offset: updatedFilter.pagination ? updatedFilter.pagination.offset : 0
    }
  });
}

/**
 * Returns the base filter state for a given UI mode. Used for initializing state
 * (e.g. when no view is loaded) and for resetting when switching modes (e.g. list ↔ folder).
 */
export function getInitialFiltersStateForUIMode({
  initialFilter,
  uiMode
}) {
  return Object.assign({}, INITIAL_FILTER_STATE, initialFilter || {}, {
    pagination: getInitialPagination({
      initialFilter,
      uiMode
    }),
    query: initialFilter && initialFilter.query ? initialFilter.query : ''
  });
}
export function makeFiltersState({
  initialFilter,
  currentView,
  uiMode
}) {
  const viewFilterState = viewToFilterState(currentView);
  return combineFilterStates({
    initialFilter,
    newFilter: viewFilterState,
    uiMode
  });
}

/**
 * This function accepts both the initial filters passed in from the provider and the filters from the current
 * view. It will then remove any filters from the current view that are filtering a property that the initial filter is
 * also filtering. It then returns the new current view filters.
 * @param initialQuickFilters
 * @param currentViewQuickFilters
 */
export function excludeFiltersOfSameProperty(initialQuickFilters, currentViewQuickFilters) {
  const initialFilterProperties = initialQuickFilters.map(filterOperator => filterOperator.property);
  const newViewFilters = currentViewQuickFilters.filter(filterOperator => !initialFilterProperties.includes(filterOperator.property));
  return flattenFilterGroups(combineFilters([{
    filters: initialQuickFilters
  }], [{
    filters: newViewFilters
  }]));
}
export function viewToFilterState(view) {
  const safeSort = view.state.sortKey && view.state.order !== undefined ? {
    property: view.state.sortKey,
    order: view.state.order === 1 ? DESC : ASC
  } : INITIAL_FILTER_STATE.sort;
  return {
    filterGroups: view.filterGroups && view.filterGroups.length ? view.filterGroups : [{
      filters: view.filters
    }],
    quickFilters: view.quickFilters || [],
    sort: safeSort
  };
}
export function combineFilterStates({
  initialFilter,
  newFilter,
  uiMode
}) {
  const filterGroupsFromInitialFilter = initialFilter && initialFilter.filterGroups ? initialFilter.filterGroups : [{
    filters: []
  }];
  const newFilterGroups = newFilter.filterGroups && newFilter.filterGroups.length ? newFilter.filterGroups : [{
    filters: []
  }];
  const quickFiltersFromInitialFilter = initialFilter && initialFilter.quickFilters ? initialFilter.quickFilters : INITIAL_FILTER_STATE.quickFilters;
  const newQuickFilters = newFilter.quickFilters || [];
  const newSortOrder = newFilter.sort && newFilter.sort.order && newFilter.sort.order === 'DESC' ? DESC : ASC;
  let sortingProperty = INITIAL_FILTER_STATE.sort.property;
  if (initialFilter && initialFilter.sort) {
    sortingProperty = initialFilter.sort.property;
  } else if (newFilter && newFilter.sort) {
    sortingProperty = newFilter.sort.property;
  }
  return Object.assign({}, INITIAL_FILTER_STATE, {
    filterGroups: combineFilters(filterGroupsFromInitialFilter, newFilterGroups),
    requestOptions: initialFilter && initialFilter.requestOptions ? initialFilter.requestOptions : INITIAL_FILTER_STATE.requestOptions,
    sort: {
      property: sortingProperty,
      order: initialFilter && initialFilter.sort ? initialFilter.sort.order : newSortOrder
    },
    pagination: getInitialPagination({
      initialFilter,
      uiMode
    }),
    query: initialFilter && initialFilter.query ? initialFilter.query : INITIAL_FILTER_STATE.query,
    quickFilters: excludeFiltersOfSameProperty(quickFiltersFromInitialFilter, newQuickFilters),
    associationPreviews: initialFilter && initialFilter.associationPreviews ? initialFilter.associationPreviews : INITIAL_FILTER_STATE.associationPreviews
  });
}